import React, { useMemo } from "react";

import { transformScreenAnalytics } from "../utils/analyticsUtils";

function HBar({ label, value, max, color, sub, delay = 0 }) {
  const pct = Math.round((value / Math.max(max, 1)) * 100);
  return (
    <div className="ua-hbar">
      <div className="ua-hbar-row">
        <span className="ua-hbar-label">{label}</span>
        <span className="ua-hbar-val">
          {value}
          {sub && <span style={{ opacity:.55, marginLeft:4 }}>· {sub}</span>}
        </span>
      </div>
      <div className="ua-hbar-track">
        <div className="ua-hbar-fill" style={{ width:`${pct}%`, background:color, animationDelay:`${delay}s` }} />
      </div>
    </div>
  );
}

function Card({ title, sub, accent = "p", delay = 0, children }) {
  return (
    <div className={`ua-card ${accent}`} style={{ animationDelay:`${delay}s` }}>
      <div className="ua-card-head">
        <span className="ua-card-title">{title}</span>
        {sub && <span className="ua-card-sub">{sub}</span>}
      </div>
      {children}
    </div>
  );
}

export default function ScreensTab({ data }) {
  const screenDist = useMemo(() => transformScreenAnalytics(data?.screen_analytics), [data]);
  const maxViews   = Math.max(...screenDist.map(s => s.views), 1);

  return (
    <div className="ua-vstack">

      {/* Screen cards grid */}
      <div className="ua-grid-3">
        {screenDist.length === 0
          ? <p style={{ color:"#9B99C4", fontSize:13, gridColumn:"1/-1" }}>No screen data.</p>
          : screenDist.map((s, i) => (
              <div key={s.screen} className="ua-sc-card" style={{ animationDelay:`${i*.06}s` }}>
                {/* Accent top bar */}
                <div style={{
                  position:"absolute", top:0, left:0, right:0, height:3,
                  borderRadius:"var(--r-lg) var(--r-lg) 0 0",
                  background: s.color,
                }} />
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div className="ua-sc-name">{s.screen}</div>
                    <div className="ua-sc-sessions">{s.sessions} unique session{s.sessions!==1?"s":""}</div>
                  </div>
                  <div className="ua-sc-count" style={{ color: s.color }}>{s.views}</div>
                </div>
                <div className="ua-hbar-track">
                  <div className="ua-hbar-fill" style={{
                    width:`${Math.round((s.views/maxViews)*100)}%`,
                    background: s.color,
                    animationDelay:`${i*.07}s`,
                  }} />
                </div>
                <div style={{ fontSize:11, color:"#9B99C4", marginTop:6 }}>
                  {Math.round((s.views/maxViews)*100)}% of peak
                </div>
              </div>
            ))
        }
      </div>

      {/* Ranked bar list */}
      <Card title="Screen views" sub="ranked by visits" accent="n" delay={0.15}>
        {screenDist.length === 0
          ? <p style={{ color:"#9B99C4", fontSize:13 }}>No screen data.</p>
          : screenDist.map((s, i) => (
              <HBar key={s.screen} label={s.screen} value={s.views} max={maxViews}
                color={s.color} sub={`${s.sessions} session${s.sessions!==1?"s":""}`} delay={i*.07} />
            ))
        }
      </Card>

      {/* Side by side bar chart */}
      <Card title="Screen engagement" sub="views vs unique sessions" accent="b" delay={0.20}>
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(screenDist.length,7)},minmax(0,1fr))`, gap:8, alignItems:"flex-end" }}>
          {screenDist.map((s, i) => {
            const maxH=100, vH=Math.round((s.views/maxViews)*maxH), sH=Math.round((s.sessions/maxViews)*maxH);
            return (
              <div key={s.screen} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:maxH }}>
                  <div style={{ width:14, height:`${vH}px`, background:"#6366F1", borderRadius:"4px 4px 0 0", opacity:.9 }} />
                  <div style={{ width:14, height:`${sH}px`, background:"#10B981", borderRadius:"4px 4px 0 0", opacity:.9 }} />
                </div>
                <span style={{ fontSize:10, color:"#9B99C4", textAlign:"center", wordBreak:"break-word", lineHeight:1.3 }}>{s.screen}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:16, marginTop:14, fontSize:12, color:"#9B99C4" }}>
          <span style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:10, height:10, borderRadius:2, background:"#6366F1", display:"inline-block" }} />Views
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:10, height:10, borderRadius:2, background:"#10B981", display:"inline-block" }} />Sessions
          </span>
        </div>
      </Card>

      {/* Screen flow */}
      <Card title="Screen flow" sub="typical user path" accent="t" delay={0.25}>
        <div className="ua-flow-wrap">
          {screenDist.map((s, i, arr) => (
            <div key={s.screen} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div className="ua-flow-chip" style={{
                background: s.color + "18",
                color: s.color,
                border: `1px solid ${s.color}33`,
              }}>{s.screen}</div>
              {i < arr.length-1 && <span className="ua-flow-arr">→</span>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}